import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Lock, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { LOGO, CONTACTS } from "@/data";
import { useLang } from "@/i18n";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { label: t.nav.services, id: "services" },
    { label: t.nav.work, id: "portfolio" },
    { label: t.nav.why, id: "manifesto" },
    { label: t.nav.reviews, id: "testimonials" },
    { label: t.nav.contact, id: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleLang = () => setLang(lang === "es" ? "en" : "es");

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/60 border-b border-[#d4af37]/20" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img src={LOGO} alt="Banman's Painting" className="h-11 w-11 rounded-full object-cover ring-1 ring-[#d4af37]/40" />
          <span className="font-serif-lux text-xl tracking-wide text-white hidden sm:block">
            Banman&apos;s <span className="text-[#d4af37]">Painting</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="relative text-sm uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#d4af37] after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            data-testid="nav-lang-toggle"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-white/70 hover:text-[#d4af37] transition-colors duration-300"
            aria-label="Toggle language"
          >
            <Globe size={14} /> {lang === "es" ? "EN" : "ES"}
          </button>
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-white/50 hover:text-[#d4af37] transition-colors duration-300"
          >
            <Lock size={12} /> {t.nav.admin}
          </Link>
          <a
            href={`tel:${CONTACTS[0].tel}`}
            data-testid="nav-call-btn"
            className="hidden md:inline-flex items-center gap-2 border border-[#d4af37]/40 text-[#d4af37] px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-[#d4af37] hover:text-black transition-colors duration-300"
          >
            <Phone size={14} /> {t.nav.estimate}
          </a>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="md:hidden overflow-hidden bg-black/95 border-t border-[#d4af37]/20"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="text-left font-serif-lux text-2xl text-white/90"
                >
                  {l.label}
                </button>
              ))}
              <a href={`tel:${CONTACTS[0].tel}`} className="mt-2 inline-flex items-center gap-2 text-[#d4af37] uppercase tracking-widest text-sm">
                <Phone size={16} /> {t.nav.callEstimate}
              </a>
              <Link to="/admin" data-testid="nav-mobile-admin-link" className="inline-flex items-center gap-2 text-white/50 uppercase tracking-widest text-sm">
                <Lock size={14} /> {t.nav.adminLogin}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
